// NOTE: NSAI-7345 — mirrors the server's ETextExtractionWarningCode (same
// values): why a PDF's extracted text looks incomplete for its page count.
// Clients show the translation, never a server sentence.
export enum ETextExtractionWarningCode {
  // NOTE: Far less text overall than the page count implies.
  LOW_TEXT = "low_text",
  // NOTE: Most pages have little or no text — typically scanned pages mixed
  // into a typed document.
  PAGES_WITHOUT_TEXT = "pages_without_text",
}

export interface ITextExtractionWarning {
  code: ETextExtractionWarningCode;
  pageCount: number;
  // NOTE: Characters extracted, whitespace and control characters excluded.
  textCharCount: number;
  // NOTE: Pages with little or no text; null when only the total is known.
  lowTextPageCount: number | null;
}

// NOTE: The i18n key a warning is shown with, one per code and surface. The
// chat names the attachment; the Knowledge lists show it on the file's row.
export type TChatTextExtractionWarningI18nKey =
  | "chat:textExtractionWarning.low_text"
  | "chat:textExtractionWarning.pages_without_text";

export type TKnowledgeTextExtractionWarningI18nKey =
  | "knowledge:textExtractionWarnings.low_text"
  | "knowledge:textExtractionWarnings.pages_without_text";

// NOTE: The values a warning's sentence interpolates, numbers already
// formatted for the reader's language. A type, not an interface: i18next's
// options demand an index signature, which only a type alias gets implicitly
// (as TManusAutomationNoticeParams).
export type TTextExtractionWarningValues = {
  pageCount: string;
  textCharCount: string;
  lowTextPageCount: string;
};

// NOTE: A warning ready to translate: its key and the values the sentence
// interpolates.
export interface ITextExtractionWarningText<TKey extends string> {
  key: TKey;
  values: TTextExtractionWarningValues;
}

// NOTE: Two warnings to compare — the one an attachment carries (if any) and
// the one an event brings.
export interface IIsSameTextExtractionWarningProps {
  current: ITextExtractionWarning | undefined;
  next: ITextExtractionWarning;
}

export interface IGetTextExtractionWarningTextProps {
  warning: ITextExtractionWarning;
  language: string;
}

// NOTE: One attachment of a chat message and the warning on its text.
export interface IAttachmentTextExtractionWarning {
  fileUuid: string;
  warning: ITextExtractionWarning;
}

// NOTE: Payload of ATTACHMENT_TEXT_EXTRACTION_WARNING_SOCKET_EVENT. Keep in sync
// with the server's chat/types/text-extraction-warning.types.ts.
export interface IAttachmentTextExtractionWarningPayload {
  chatUuid: string;
  // NOTE: The user message the attachments belong to.
  messageUuid: string;
  files: IAttachmentTextExtractionWarning[];
}
